import type StyleSheet from '../sheet';
import {
  AnyComponent,
  Dict,
  ExecutionContext,
  Interpolation,
  IStyledComponent,
  RuleSet,
  Stringifier,
} from '../types';
import { cssTagged } from './cssTagged';
import addUnitIfNeeded from './addUnitIfNeeded';
import getComponentName from './getComponentName';
import hyphenate from './hyphenateStyleName';
import isFunction from './isFunction';
import isKeyframes from './isKeyframes';
import isPlainObject from './isPlainObject';
import isStatelessFunction from './isStatelessFunction';
import isStyledComponent from './isStyledComponent';

/**
 * It's falsish not falsy because 0 is allowed.
 */
const isFalsish = (chunk: any): chunk is undefined | null | false | '' =>
  chunk === undefined || chunk === null || chunk === false || chunk === '';

const CLIENT_REFERENCE = Symbol.for('react.client.reference');

interface ClientReference {
  $$typeof: symbol;
  $$id?: string;
  $$async?: boolean;
  name?: string;
}

function isClientReference(chunk: unknown): chunk is ClientReference {
  return (chunk as any).$$typeof === CLIENT_REFERENCE;
}

// React encodes $$id as "modulePath#exportName"
function warnClientReference(ref: ClientReference): void {
  const id = ref.$$id;
  const label = (id && id.includes('#') ? id.split('#').pop() : id) || ref.name || 'unknown';
  console.warn(
    `Interpolating a client component (${label}) as a selector is not supported in server components. The component selector pattern requires access to the component's internal class name, which is not available across the server/client boundary. Use a plain CSS class selector instead.`
  );
}

/** Internal accumulator form - avoids allocating a temp array per nesting level. */
function objToCssArrayInto(obj: Dict<any>, rules: any[]): void {
  for (const key in obj) {
    const val = obj[key];
    if (!obj.hasOwnProperty(key) || isFalsish(val)) continue;

    if ((Array.isArray(val) && cssTagged.has(val)) || isFunction(val)) {
      rules.push(hyphenate(key) + ':', val, ';');
    } else if (isPlainObject(val)) {
      rules.push(key + ' {');
      objToCssArrayInto(val, rules);
      rules.push('}');
    } else {
      rules.push(hyphenate(key) + ': ' + addUnitIfNeeded(key, val) + ';');
    }
  }
}

export const objToCssArray = (obj: Dict<any>): string[] => {
  const rules: any[] = [];
  objToCssArrayInto(obj, rules);
  return rules;
};

export default function flatten<Props extends object>(
  chunk: Interpolation<object>,
  executionContext?: (ExecutionContext & Props) | undefined,
  styleSheet?: StyleSheet | undefined,
  stylisInstance?: Stringifier | undefined,
  result: RuleSet<Props> = []
): RuleSet<Props> {
  if (isFalsish(chunk)) {
    return result;
  }

  const t = typeof chunk;

  if (t === 'string') {
    result.push(chunk as string);
    return result;
  }

  if (t === 'function') {
    if (isClientReference(chunk)) {
      if (process.env.NODE_ENV !== 'production') warnClientReference(chunk);
      return result;
    }

    if (isStatelessFunction(chunk) && executionContext) {
      const fnResult = (chunk as Function)(executionContext);

      if (
        process.env.NODE_ENV !== 'production' &&
        typeof fnResult === 'object' &&
        !Array.isArray(fnResult) &&
        !isKeyframes(fnResult) &&
        !isPlainObject(fnResult) &&
        fnResult !== null
      ) {
        console.error(
          `${getComponentName(
            chunk as AnyComponent
          )} is not a styled component and cannot be referred to via component selector. See https://styled-components.com/docs/advanced#referring-to-other-components for more details.`
        );
      }

      return flatten<Props>(fnResult, executionContext, styleSheet, stylisInstance, result);
    } else {
      result.push(chunk as unknown as IStyledComponent<'web'>);
      return result;
    }
  }

  if (Array.isArray(chunk)) {
    for (let i = 0; i < chunk.length; i++) {
      flatten<Props>(chunk[i], executionContext, styleSheet, stylisInstance, result);
    }
    return result;
  }

  if (isStyledComponent(chunk)) {
    result.push(`.${(chunk as unknown as IStyledComponent<'web', any>).styledComponentId}`);
    return result;
  }

  if (isKeyframes(chunk)) {
    if (styleSheet) {
      chunk.inject(styleSheet, stylisInstance);
      result.push(chunk.getName(stylisInstance));
    } else {
      result.push(chunk);
    }
    return result;
  }

  // Module-level client reference proxies (typeof 'object') pass isPlainObject - catch before
  if (isClientReference(chunk)) {
    if (process.env.NODE_ENV !== 'production') warnClientReference(chunk);
    return result;
  }

  if (isPlainObject(chunk)) {
    // A plain object that defines its own toString takes the stringification
    // path - it's being interpolated as a value (e.g. a design token), not as
    // a CSS-in-JS rule object. Without this, `${token}` in a tagged template
    // would walk the object's enumerable keys as CSS declarations. (#5740)
    if ((chunk as object).toString !== Object.prototype.toString) {
      result.push((chunk as object).toString());
      return result;
    }
    objToCssArrayInto(chunk as Dict<any>, result);
    return result;
  }

  result.push((chunk as any).toString());
  return result;
}
